import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Popover,
  List,
  Tag,
  Typography,
  Empty,
  Spin,
  Space,
} from "antd";
import {
  BellOutlined,
  CheckOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { alertsApi, Alerta, AlertaNivel } from "../services/alerts.api";
import { useWebSocket } from "../context/WebSocketContext";
import { getMsg } from "../utils/antdStatic";

const { Text, Paragraph } = Typography;

const NIVEL_COLOR: Record<AlertaNivel, string> = {
  critica: "red",
  advertencia: "orange",
  info: "blue",
};

/**
 * Campana de alertas administrativas (header). Badge rojo con el número de
 * alertas activas + panel con las más recientes y acciones rápidas.
 * Realtime vía WS `ALERTS_CHANGED`. Sólo se monta para admins (ver Header).
 * Ver docs/modulo-alertas.md.
 */
const AlertsBell: React.FC = () => {
  const navigate = useNavigate();
  const { socket } = useWebSocket();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Alerta[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);

  const refreshCount = useCallback(async () => {
    const n = await alertsApi.contador();
    setCount(n);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    const rows = await alertsApi.listar("activa", 8);
    setItems(rows);
    setLoading(false);
  }, []);

  // Carga inicial + polling de respaldo (por si se pierde un WS).
  useEffect(() => {
    refreshCount();
    const id = window.setInterval(refreshCount, 60_000);
    return () => window.clearInterval(id);
  }, [refreshCount]);

  // Realtime: cualquier cambio de alertas refresca el contador (y la lista
  // si el panel está abierto).
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (!socket) return;
    const onMessage = (e: MessageEvent) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg?.type !== "ALERTS_CHANGED") return;
        refreshCount();
        if (openRef.current) loadList();
      } catch {
        /* mensaje no-JSON: ignorar */
      }
    };
    socket.addEventListener("message", onMessage);
    return () => socket.removeEventListener("message", onMessage);
  }, [socket, refreshCount, loadList]);

  const onOpenChange = (v: boolean) => {
    setOpen(v);
    if (v) loadList();
  };

  const resolver = async (a: Alerta) => {
    setBusyId(a.id);
    await alertsApi.resolver(a.id);
    getMsg()?.success("Alerta resuelta");
    setBusyId(null);
    await Promise.all([refreshCount(), loadList()]);
  };

  const reprocesar = async (a: Alerta) => {
    const listing = a.metadata?.id_propiedad_largo;
    if (!listing) {
      getMsg()?.warning("Esta alerta no tiene un listing asociado");
      return;
    }
    setBusyId(a.id);
    const res = await alertsApi.reprocesarPropiedad(String(listing));
    setBusyId(null);
    if (res) {
      getMsg()?.success(
        `Se reencolaron ${res.reset} webhook(s) de esa propiedad. El cron los reprocesa en ~1 min.`,
      );
    }
  };

  const panel = (
    <div style={{ width: 380, maxWidth: "90vw" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text strong>Alertas activas</Text>
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          onClick={loadList}
          aria-label="Refrescar alertas"
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin alertas activas"
          style={{ padding: "12px 0" }}
        />
      ) : (
        <List
          size="small"
          dataSource={items}
          style={{ maxHeight: 360, overflowY: "auto" }}
          renderItem={(a) => (
            <List.Item style={{ display: "block", padding: "8px 4px" }}>
              <Space size={6} style={{ marginBottom: 2 }}>
                <Tag color={NIVEL_COLOR[a.nivel]} style={{ marginInlineEnd: 0 }}>
                  {a.nivel}
                </Tag>
                <Text strong style={{ fontSize: 13 }}>
                  {a.titulo}
                </Text>
                {a.contador > 1 && <Tag>{a.contador}×</Tag>}
              </Space>
              {a.mensaje && (
                <Paragraph
                  type="secondary"
                  // `pre-line` respeta los saltos del mensaje: las alertas de
                  // sincronización con Stays son una ficha de varias líneas, y
                  // las 3 que caben acá son justo las que identifican el
                  // registro (qué es / propiedad / fechas). Sin esto se pegan
                  // en un párrafo corrido y se pierde el resumen.
                  style={{ fontSize: 12, margin: "2px 0 6px", whiteSpace: "pre-line" }}
                  ellipsis={{ rows: 3, expandable: true, symbol: "ver más" }}
                >
                  {a.mensaje}
                </Paragraph>
              )}
              <Space size={6} wrap>
                {a.categoria === "stays_property_not_found" && (
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={busyId === a.id}
                    onClick={() => reprocesar(a)}
                  >
                    Reprocesar
                  </Button>
                )}
                <Button
                  size="small"
                  type="text"
                  icon={<CheckOutlined />}
                  loading={busyId === a.id}
                  onClick={() => resolver(a)}
                >
                  Resolver
                </Button>
              </Space>
            </List.Item>
          )}
        />
      )}

      <div style={{ borderTop: "1px solid #f0f0f0", marginTop: 8, paddingTop: 8 }}>
        <Button
          type="link"
          size="small"
          block
          onClick={() => {
            setOpen(false);
            navigate("/alertas");
          }}
        >
          Ver todas las alertas →
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={panel}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={onOpenChange}
    >
      <Badge count={count} size="small" overflowCount={99}>
        <Button
          type="text"
          shape="circle"
          aria-label="Alertas"
          icon={
            count > 0 ? (
              <WarningOutlined style={{ fontSize: 18, color: "#cf1322" }} />
            ) : (
              <BellOutlined style={{ fontSize: 18 }} />
            )
          }
        />
      </Badge>
    </Popover>
  );
};

export default AlertsBell;
